/**
 * Weapon definitions — the engine-facing table.
 *
 * Twenty-three weapons, and almost none of the numbers in here are typed in. The
 * stats that came off the reference, the stats derived from them, and the stats we
 * invented all live in `weapons.stats.js`, kept in three labelled blocks; this file
 * is the assembly line that turns those into the shape `WeaponSystem`,
 * `Ballistics` and `ViewModel` expect. If you want to know where a number came
 * from, that file answers it; if you want to know what a number *does*, this one
 * does.
 *
 * The one thing genuinely authored here is the recoil pattern tables, because they
 * are shapes rather than scalars and reading them as literals is the only way to
 * see what a weapon does to your aim.
 *
 *   `pattern` — a deterministic per-shot recoil offset table. Every burst walks the
 *   same path, so a player can learn to pull against it. Random-only recoil is the
 *   single most common reason a shooter feels broken; the random component here is
 *   deliberately tiny and only breaks up the pattern's edges. Measured across the
 *   roster, the pattern sum dominates the noise 33:1 to 44:1 at shot 30 — see
 *   `patternQuality()` at the bottom of this file, which computes it.
 *
 *   `spread` — the cone by stance. ADS + stationary is exactly zero for every
 *   weapon in the file including the shotguns, which is non-negotiable: the first
 *   ADS shot must land on the crosshair every time. A shotgun's pellets scatter
 *   because of `pelletSpread`, which is a property of the shell and is applied by
 *   `Ballistics` on top of an aim cone that is still exactly zero.
 *
 * Angles are radians, distances metres, times seconds.
 */

import {
  SCRAPED,
  DERIVED,
  AUTHORED,
  CLASS_BASE,
  WEAPON_CLASSES,
  ROSTER,
} from './weapons.stats.js';

export { WEAPON_CLASSES };

/* ---------------------------------------------------------------------------- *
 * Recoil patterns. [pitch, yaw] per shot. These are *increments*: shot N's entry is
 * added to the climb shots 1..N-1 already put there, so the visible path of the
 * muzzle is the running sum of the table, not the table itself. That is why every
 * pitch column falls away after the first handful of entries — the climb plateaus,
 * it does not reverse. Reading a table as absolute offsets inverts every pattern in
 * this file.
 *
 * Pitch is positive = muzzle climbs. Yaw is positive = drifts right.
 *
 * The magnitudes are *not* here. Each table is normalised so its first entry is
 * about 1.0, and `recoil.pitch` — computed in `build()` from the weapon's authored
 * `climb30Deg` — is what says how many degrees that means. Shape and size are two
 * different decisions and retuning one must not silently move the other.
 *
 * Shapes by class:
 *   rifles     a hard climb for three or four rounds, then a plateau, with the yaw
 *              arcing one way over a dozen rounds and hooking back. The classic
 *              learnable "7", with a different period and drift direction per gun.
 *   SMGs       a lower plateau and a yaw column that alternates sign every single
 *              round, so the muzzle shimmies rather than arcs. Controllable inside
 *              20 m and hopeless past it, which is the intent.
 *   LMGs       48 entries, because a 60- or 75-round magazine would otherwise clamp
 *              on the last row a third of the way through the belt. The last yaw is
 *              pinned to 0: that entry repeats for every round past the table, and
 *              any other value would turn sustained fire into an unlearnable
 *              one-way drift.
 *   shotguns   one enormous kick, near enough identical every shell.
 *   marksman   big, slow, mostly vertical; only the first few entries are ever seen.
 *   snipers    six entries of identical size with an alternating yaw, so a panicked
 *              double-tap returns to where it started instead of being random.
 * ---------------------------------------------------------------------------- */

const P_AK27 = [
  [1.04, 0.00], [1.03, -0.30], [0.93, -0.56], [0.80, -0.76], [0.68, -0.88], [0.57, -0.92],
  [0.48, -0.88], [0.41, -0.77], [0.36, -0.59], [0.33, -0.37], [0.31, -0.12], [0.30, 0.12],
  [0.30, 0.35], [0.29, 0.54], [0.29, 0.68], [0.29, 0.75], [0.29, 0.75], [0.29, 0.69],
  [0.29, 0.58], [0.29, 0.41], [0.29, 0.21], [0.29, 0.00], [0.29, -0.21], [0.29, -0.39],
  [0.29, -0.54], [0.29, -0.63], [0.29, -0.67], [0.29, -0.65], [0.29, -0.58], [0.29, -0.45],
];

const P_X9MAVERICK = [
  [1.10, 0.41], [1.07, 0.67], [0.90, 0.79], [0.69, 0.78], [0.53, 0.64], [0.41, 0.39],
  [0.33, 0.09], [0.29, -0.21], [0.27, -0.46], [0.26, -0.63], [0.26, -0.68], [0.26, -0.62],
  [0.26, -0.45], [0.26, -0.22], [0.26, 0.05], [0.26, 0.30], [0.26, 0.49], [0.26, 0.59],
  [0.26, 0.59], [0.26, 0.49], [0.26, 0.31], [0.26, 0.07], [0.26, -0.16], [0.26, -0.37],
  [0.26, -0.51], [0.26, -0.56], [0.26, -0.51], [0.26, -0.38], [0.26, -0.18], [0.26, 0.04],
];

const P_DS20MIRAGE = [
  [1.01, 0.77], [1.01, 0.88], [0.93, 0.92], [0.82, 0.90], [0.72, 0.82], [0.63, 0.69],
  [0.54, 0.53], [0.47, 0.33], [0.42, 0.12], [0.38, -0.09], [0.35, -0.29], [0.33, -0.46],
  [0.32, -0.59], [0.32, -0.68], [0.31, -0.72], [0.31, -0.71], [0.31, -0.66], [0.31, -0.56],
  [0.31, -0.43], [0.31, -0.27], [0.31, -0.10], [0.31, 0.08], [0.31, 0.24], [0.31, 0.39],
  [0.31, 0.50], [0.31, 0.58], [0.31, 0.62], [0.31, 0.62], [0.31, 0.58], [0.31, 0.49],
];

const P_PEACEKEEPERMK1 = [
  [0.95, -0.95], [0.94, -0.70], [0.89, -0.39], [0.81, -0.05], [0.73, 0.28], [0.66, 0.56],
  [0.59, 0.77], [0.52, 0.89], [0.47, 0.91], [0.43, 0.83], [0.40, 0.67], [0.37, 0.45],
  [0.36, 0.18], [0.35, -0.10], [0.34, -0.35], [0.34, -0.56], [0.33, -0.71], [0.33, -0.77],
  [0.33, -0.76], [0.33, -0.66], [0.33, -0.49], [0.33, -0.28], [0.33, -0.04], [0.33, 0.20],
  [0.33, 0.41], [0.33, 0.58], [0.33, 0.68], [0.33, 0.70], [0.33, 0.65], [0.33, 0.53],
];

const P_M15MOD0 = [
  [1.06, 0.89], [1.04, 0.87], [0.92, 0.79], [0.76, 0.66], [0.62, 0.49], [0.51, 0.29],
  [0.42, 0.08], [0.36, -0.13], [0.32, -0.32], [0.30, -0.48], [0.29, -0.60], [0.28, -0.67],
  [0.28, -0.69], [0.28, -0.66], [0.28, -0.58], [0.28, -0.47], [0.28, -0.32], [0.28, -0.15],
  [0.28, 0.02], [0.28, 0.19], [0.28, 0.34], [0.28, 0.46], [0.28, 0.54], [0.28, 0.59],
  [0.28, 0.59], [0.28, 0.54], [0.28, 0.46], [0.28, 0.35], [0.28, 0.21], [0.28, 0.06],
];

const P_MXR17 = [
  [1.15, -0.23], [1.16, -0.39], [1.07, -0.52], [0.94, -0.62], [0.83, -0.67], [0.73, -0.69],
  [0.64, -0.66], [0.56, -0.60], [0.49, -0.51], [0.44, -0.39], [0.40, -0.26], [0.38, -0.11],
  [0.36, 0.03], [0.35, 0.17], [0.35, 0.30], [0.34, 0.40], [0.34, 0.48], [0.34, 0.53],
  [0.34, 0.55], [0.34, 0.53], [0.34, 0.49], [0.34, 0.42], [0.34, 0.32], [0.34, 0.21],
  [0.34, 0.09], [0.34, -0.03], [0.34, -0.15], [0.34, -0.26], [0.34, -0.35], [0.34, -0.42],
];

const P_RAZOR9MM = [
  [0.81, 0.06], [0.79, -0.65], [0.70, 0.77], [0.59, -0.86], [0.49, 0.91], [0.42, -0.91],
  [0.37, 0.86], [0.33, -0.77], [0.32, 0.65], [0.31, -0.51], [0.30, 0.36], [0.30, -0.24],
  [0.30, 0.15], [0.30, -0.10], [0.30, 0.10], [0.30, -0.15], [0.30, 0.24], [0.30, -0.36],
  [0.30, 0.51], [0.30, -0.65], [0.30, 0.77], [0.30, -0.86], [0.30, 0.91], [0.30, -0.91],
  [0.30, 0.86], [0.30, -0.77], [0.30, 0.65], [0.30, -0.51], [0.30, 0.36], [0.30, -0.24],
  [0.30, 0.15], [0.30, -0.10], [0.30, 0.10], [0.30, -0.15],
];

const P_MPC25 = [
  [0.87, 0.09], [0.84, -0.83], [0.72, 0.86], [0.57, -0.86], [0.45, 0.83], [0.38, -0.77],
  [0.33, 0.68], [0.31, -0.58], [0.30, 0.47], [0.29, -0.36], [0.29, 0.26], [0.29, -0.18],
  [0.29, 0.12], [0.29, -0.09], [0.29, 0.09], [0.29, -0.12], [0.29, 0.18], [0.29, -0.26],
  [0.29, 0.36], [0.29, -0.47], [0.29, 0.58], [0.29, -0.68], [0.29, 0.77], [0.29, -0.83],
  [0.29, 0.86], [0.29, -0.86], [0.29, 0.83], [0.29, -0.77], [0.29, 0.68], [0.29, -0.58],
  [0.29, 0.47], [0.29, -0.36], [0.29, 0.26], [0.29, -0.18],
];

const P_RK9 = [
  [0.91, 0.09], [0.89, -0.74], [0.82, 0.74], [0.71, -0.72], [0.62, 0.68], [0.53, -0.63],
  [0.46, 0.56], [0.41, -0.49], [0.38, 0.41], [0.36, -0.33], [0.34, 0.25], [0.34, -0.19],
  [0.33, 0.13], [0.33, -0.10], [0.33, 0.08], [0.33, -0.08], [0.33, 0.10], [0.33, -0.13],
  [0.33, 0.19], [0.33, -0.25], [0.33, 0.33], [0.33, -0.41], [0.33, 0.49], [0.33, -0.56],
  [0.33, 0.63], [0.33, -0.68], [0.33, 0.72], [0.33, -0.74], [0.33, 0.74], [0.33, -0.72],
  [0.33, 0.68], [0.33, -0.63], [0.33, 0.56], [0.33, -0.49],
];

const P_DRAVEC45 = [
  [0.85, 0.09], [0.84, -0.85], [0.75, 0.88], [0.63, -0.85], [0.53, 0.76], [0.45, -0.64],
  [0.39, 0.48], [0.36, -0.33], [0.33, 0.20], [0.32, -0.12], [0.31, 0.09], [0.31, -0.12],
  [0.31, 0.20], [0.31, -0.33], [0.31, 0.48], [0.31, -0.64], [0.31, 0.76], [0.31, -0.85],
  [0.31, 0.88], [0.31, -0.85], [0.31, 0.76], [0.31, -0.64], [0.31, 0.48], [0.31, -0.33],
  [0.31, 0.20], [0.31, -0.12], [0.31, 0.09], [0.31, -0.12], [0.31, 0.20], [0.31, -0.33],
  [0.31, 0.48], [0.31, -0.64], [0.31, 0.76], [0.31, -0.85],
];

const P_RYDEN45K = [
  [0.83, 0.11], [0.80, -0.87], [0.70, 0.77], [0.58, -0.65], [0.47, 0.52], [0.39, -0.39],
  [0.34, 0.27], [0.31, -0.18], [0.29, 0.12], [0.28, -0.10], [0.28, 0.12], [0.28, -0.18],
  [0.28, 0.27], [0.28, -0.39], [0.28, 0.52], [0.28, -0.65], [0.28, 0.77], [0.28, -0.87],
  [0.28, 0.93], [0.28, -0.95], [0.28, 0.93], [0.28, -0.87], [0.28, 0.77], [0.28, -0.65],
  [0.28, 0.52], [0.28, -0.39], [0.28, 0.27], [0.28, -0.18], [0.28, 0.12], [0.28, -0.10],
  [0.28, 0.12], [0.28, -0.18], [0.28, 0.27], [0.28, -0.39],
];

const P_CARBON57 = [
  [0.78, 0.06], [0.77, -0.62], [0.72, 0.69], [0.64, -0.75], [0.57, 0.79], [0.51, -0.80],
  [0.45, 0.79], [0.41, -0.75], [0.37, 0.69], [0.35, -0.62], [0.34, 0.53], [0.33, -0.44],
  [0.32, 0.35], [0.32, -0.26], [0.32, 0.19], [0.32, -0.13], [0.32, 0.09], [0.32, -0.08],
  [0.32, 0.09], [0.32, -0.13], [0.32, 0.19], [0.32, -0.26], [0.32, 0.35], [0.32, -0.44],
  [0.32, 0.53], [0.32, -0.62], [0.32, 0.69], [0.32, -0.75], [0.32, 0.79], [0.32, -0.80],
  [0.32, 0.79], [0.32, -0.75], [0.32, 0.69], [0.32, -0.62],
];

const P_XM325 = [
  [0.99, 0.18], [0.99, 0.44], [0.92, 0.64], [0.83, 0.78], [0.74, 0.84], [0.65, 0.81],
  [0.57, 0.71], [0.50, 0.54], [0.43, 0.33], [0.39, 0.09], [0.35, -0.15], [0.32, -0.37],
  [0.31, -0.55], [0.30, -0.68], [0.29, -0.73], [0.29, -0.71], [0.28, -0.62], [0.28, -0.48],
  [0.28, -0.29], [0.28, -0.08], [0.28, 0.14], [0.28, 0.33], [0.28, 0.49], [0.28, 0.60],
  [0.28, 0.65], [0.28, 0.64], [0.28, 0.56], [0.28, 0.43], [0.28, 0.26], [0.28, 0.07],
  [0.28, -0.12], [0.28, -0.30], [0.28, -0.45], [0.28, -0.56], [0.28, -0.60], [0.28, -0.59],
  [0.28, -0.52], [0.28, -0.40], [0.28, -0.25], [0.28, -0.07], [0.28, 0.12], [0.28, 0.29],
  [0.28, 0.43], [0.28, 0.52], [0.28, 0.57], [0.28, 0.56], [0.28, 0.49], [0.28, 0.00],
];

const P_MK78 = [
  [1.08, -0.75], [1.08, -0.80], [1.02, -0.81], [0.93, -0.77], [0.85, -0.69], [0.77, -0.57],
  [0.69, -0.43], [0.61, -0.26], [0.55, -0.08], [0.49, 0.10], [0.44, 0.26], [0.40, 0.41],
  [0.37, 0.53], [0.35, 0.62], [0.34, 0.67], [0.33, 0.68], [0.32, 0.65], [0.32, 0.58],
  [0.31, 0.48], [0.31, 0.36], [0.31, 0.22], [0.31, 0.07], [0.31, -0.08], [0.31, -0.23],
  [0.31, -0.36], [0.31, -0.46], [0.31, -0.54], [0.31, -0.58], [0.31, -0.59], [0.31, -0.57],
  [0.31, -0.51], [0.31, -0.43], [0.31, -0.32], [0.31, -0.20], [0.31, -0.06], [0.31, 0.07],
  [0.31, 0.20], [0.31, 0.32], [0.31, 0.42], [0.31, 0.49], [0.31, 0.53], [0.31, 0.54],
  [0.31, 0.52], [0.31, 0.47], [0.31, 0.39], [0.31, 0.30], [0.31, 0.18], [0.31, 0.00],
];

const P_M10BREACHER = [
  [1.00, 0.01], [1.00, -0.16], [1.00, 0.20], [1.00, -0.20], [1.00, 0.16], [1.00, -0.11],
  [1.00, 0.06], [1.00, -0.02],
];

const P_ECHO12 = [
  [1.00, 0.03], [0.99, -0.26], [0.98, 0.24], [0.96, -0.20], [0.95, 0.14], [0.94, -0.08],
  [0.94, 0.04], [0.94, -0.03],
];

const P_AKITA = [
  [1.00, 0.04], [0.97, -0.34], [0.90, 0.30], [0.82, -0.19], [0.77, 0.08], [0.74, -0.03],
  [0.73, 0.08], [0.72, -0.19],
];

const P_WARDEN308 = [
  [1.00, 0.03], [1.00, -0.33], [0.99, 0.40], [0.98, -0.44], [0.96, 0.44], [0.95, -0.40],
  [0.94, 0.33], [0.93, -0.24], [0.92, 0.16], [0.91, -0.09], [0.91, 0.05], [0.90, -0.05],
  [0.90, 0.09], [0.90, -0.16],
];

const P_M8A1 = [
  [1.02, 0.06], [1.01, -0.42], [0.97, 0.29], [0.91, -0.15], [0.86, 0.06], [0.83, -0.06],
  [0.81, 0.15], [0.79, -0.29], [0.79, 0.42], [0.78, -0.51], [0.78, 0.51], [0.78, -0.42],
  [0.78, 0.29], [0.78, -0.15],
];

const P_M34NOVALINE = [
  [1.00, 0.03], [0.99, -0.32], [0.98, 0.36], [0.96, -0.38], [0.93, 0.38], [0.91, -0.36],
  [0.89, 0.32], [0.88, -0.27], [0.87, 0.21], [0.87, -0.15], [0.86, 0.10], [0.86, -0.06],
  [0.86, 0.04], [0.86, -0.04],
];

const P_VSRECON = [
  [1.00, 0.01], [1.00, -0.10], [1.00, 0.12], [1.00, -0.10], [1.00, 0.07], [1.00, -0.03],
];

const P_SHADOWSK = [
  [1.00, 0.02], [1.00, -0.16], [1.00, 0.13], [1.00, -0.09], [1.00, 0.05], [1.00, -0.02],
];

const P_XR3ION = [
  [1.00, 0.01], [1.00, -0.09], [1.00, 0.06], [1.00, -0.02], [1.00, 0.02], [1.00, -0.05],
];

/** Pattern table per weapon id. */
const PATTERNS = {
  ak27: P_AK27,
  x9maverick: P_X9MAVERICK,
  ds20mirage: P_DS20MIRAGE,
  peacekeepermk1: P_PEACEKEEPERMK1,
  m15mod0: P_M15MOD0,
  mxr17: P_MXR17,
  razor9mm: P_RAZOR9MM,
  mpc25: P_MPC25,
  rk9: P_RK9,
  dravec45: P_DRAVEC45,
  ryden45k: P_RYDEN45K,
  carbon57: P_CARBON57,
  xm325: P_XM325,
  mk78: P_MK78,
  m10breacher: P_M10BREACHER,
  echo12: P_ECHO12,
  akita: P_AKITA,
  warden308: P_WARDEN308,
  m8a1: P_M8A1,
  m34novaline: P_M34NOVALINE,
  vsrecon: P_VSRECON,
  shadowsk: P_SHADOWSK,
  xr3ion: P_XR3ION,
};

const DEG = Math.PI / 180;

/**
 * Sum of the pitch column over the first 30 rounds, or the whole table if shorter.
 *
 * Exported because the attachment resolver needs it. An attachment may scale a
 * weapon's `climb30Deg` *budget* and nothing else about its recoil, and turning that
 * budget back into a per-shot magnitude requires this exact sum — the same one
 * `build()` uses. Recomputing it there against a copy of the formula would be two
 * definitions of "how hard does this weapon kick", which is one too many.
 */
export function climbSum(pattern) {
  let s = 0;
  for (let i = 0; i < Math.min(30, pattern.length); i++) s += pattern[i][0];
  return s;
}

/* ---------------------------------------------------------------------------- *
 * Fields the attachment system writes and the behaviour systems read.
 *
 * These five blocks are on every weapon whether or not anything is attached to it,
 * with defaults that reproduce today's behaviour exactly. They exist so that the
 * gunsmith has somewhere to put a suppressor's effect, a flinch pad's effect and a
 * belt box's effect, and so that the module that implements minimap signature,
 * flinch, overheat and launcher alt-fire has a single place to read them from rather
 * than a set of `if (weapon.id === ...)` special cases.
 *
 *   `flinch.resist`   0 = today's behaviour. 1 would be immovable under fire;
 *                     negative is worse than stock, which is what a quick-ADS sniper
 *                     build is supposed to pay.
 *   `signature`       whether firing paints the shooter on the enemy minimap, how
 *                     far the shot carries for that purpose, and whether the tracer
 *                     is visible. A suppressor turns `minimap` off.
 *   `mobility`        whether the weapon may be fired out of a slide. There was a
 *                     `diveFire` beside it, removed because there is no dive: no
 *                     stance, no camera height, no hitbox set and no term for it in
 *                     AI perception, so the flag could never have paid out.
 *   `overheat`        null, or a barrel-heat model: `capacity` is normalised to 1,
 *                     `perShot` is added per round, `coolRate` is per second after
 *                     `coolDelay` seconds of not firing, and reaching capacity locks
 *                     the weapon out for `lockTime`.
 *   `launcher`        null, or an underbarrel tube for alt-fire.
 *
 * `optic` is the fitted sight. Iron sights are an optic like any other — a weapon
 * with nothing in the slot carries the block below, so nothing downstream has to
 * branch on whether a sight is fitted.
 * ---------------------------------------------------------------------------- */

/**
 * The magnification a weapon's own iron sights are worth, per class.
 *
 * This is what makes an optic's `zoom` mean something absolute. `ads.fovScale` is
 * hand-tuned per weapon and must not be overwritten, so the resolver scales it by
 * `baseZoom / zoom` instead: a 3x on a rifle is three times the rifle's own sight
 * picture, and a 7x on a sniper is seven times a 1x — which, since the sniper's
 * irons are already worth 4x, is a shade under twice what it had.
 */
export const BASE_OPTIC_ZOOM = { ar: 1.0, smg: 1.0, lmg: 1.0, sg: 1.0, mr: 2.0, sr: 4.0 };

/** Metres a shot carries for minimap purposes, per class. Authored. */
const SIGNATURE_RADIUS = { ar: 45, smg: 34, lmg: 62, sg: 34, mr: 56, sr: 76 };

/**
 * Assemble one weapon from its scraped, derived and authored parts.
 *
 * Two things happen here that are worth more than the merging:
 *
 * **Recoil magnitude comes out of the climb budget, not out of the table.**
 * `recoil.pitch` is the authored `climb30Deg` divided by the pattern's own pitch
 * sum, so the table says *where* the muzzle goes and the budget says *how far*.
 * Reshaping a pattern therefore cannot change how hard a weapon kicks, and
 * retuning the kick cannot change the shape a player has learned.
 *
 * **Bloom is authored as a net, not a gross.** `spread.perShot` is one shot's worth
 * of decay plus the authored `bloomGain`, computed against this weapon's own fire
 * rate. Bloom that adds less per shot than it decays between shots is pinned at
 * zero and silently does nothing — a bug that shipped here once and is invisible
 * from the outside, because a cone that never grows looks exactly like a weapon
 * that was designed not to bloom. Deriving it this way makes that unrepresentable:
 * a positive `bloomGain` is a positive net at any RPM.
 */
function build(id) {
  const s = SCRAPED[id];
  const d = DERIVED[id];
  const a = AUTHORED[id] ?? {};
  const base = CLASS_BASE[s.classId];
  const cls = WEAPON_CLASSES.find((c) => c.id === s.classId);
  const pattern = PATTERNS[id];

  const rpm = d.rpm;
  const spread = { ...base.spread, ...a.spread };
  // Bloom-free weapons (shotguns, snipers) keep an exact zero: the pump and the
  // bolt gate them to about one round a second, so a per-shot cone penalty would
  // only ever be a flat tax on a weapon that cannot spam anyway.
  spread.perShot = spread.maxBloom > 0 ? spread.bloomDecay * (60 / rpm) + spread.bloomGain : 0;

  const pitch = (a.climb30Deg * DEG) / climbSum(pattern);
  const recoil = {
    ...base.recoil,
    ...a.recoil,
    pitch,
    yaw: pitch * a.yawRatio,
    random: a.random ?? base.recoil.random,
    /**
     * The budget `pitch` and `yaw` were computed from, carried forward.
     *
     * Not decoration and not a duplicate: it is the *only* handle an attachment is
     * given on recoil magnitude. The resolver scales these two and re-divides by the
     * pattern's own sum, so a compensator can flatten the climb by 11% without
     * touching a single entry of the table the player has learned. Any other route
     * to `recoil.pitch` would be a route to reshaping a pattern.
     */
    climb30Deg: a.climb30Deg,
    yawRatio: a.yawRatio,
  };

  const def = {
    /* ------------------------------- identity ------------------------------ */
    id,
    name: s.name,
    className: cls.name,
    classId: s.classId,
    unlockLevel: s.unlock,
    rank: s.rank,
    /** The published time-to-kill, straight off the reference. For the UI. */
    ttk: { ...s.ttk },
    /** Which model builder to use. Equal to the id for all 23. */
    model: id,
    /** Class archetype to fall back on if this weapon's builder does not exist yet. */
    modelFallback: base.modelFallback,

    /* ------------------------------ fire control --------------------------- */
    fireMode: a.fireMode ?? base.fireMode,
    rpm,
    pellets: a.pellets ?? base.pellets ?? 1,

    /** [distance, damage]. Linearly interpolated, held flat outside the range. */
    damage: d.curve,

    magSize: s.magSize,
    startReserve: d.startReserve,
    maxReserve: d.maxReserve,
    chambered: a.chambered ?? base.chambered,

    reload: { ...base.reload, ...a.reload },
    ads: { ...base.ads, ...a.ads },
    spread,
    recoil,
    pattern,

    muzzleVelocity: a.muzzleVelocity ?? base.muzzleVelocity,
    penetration: a.penetration ?? base.penetration,

    ergonomics: { ...base.ergonomics, ...a.ergonomics },
    viewKick: { ...base.viewKick, ...a.viewKick },
    boltTravel: a.boltTravel ?? base.boltTravel,
    shell: { ...base.shell, ...a.shell },
    audio: { ...base.audio, ...a.audio },

    /**
     * Per-hitbox multipliers, applied by `Ballistics` on top of the actor's own.
     *
     * Every weapon carries a `head` entry and it is not decoration. The enemy's
     * head hitbox is x3.0, which is roughly twice what the reference's headshot
     * TTK tables imply, so without this the whole roster would kill about a shot
     * early to the head. The value is `HEAD_MULT / 3.0` — see `weapons.stats.js`,
     * where the multiplier each weapon needs is solved for and the four weapons
     * whose published headshot TTK cannot be reconciled are named.
     *
     * The one-shot weapons carry the rest of the table too, so that a bolt gun
     * that one-shots a chest does not also one-shot a shin.
     */
    hitboxScale: { head: d.headHitboxScale, ...a.hitboxScale },

    /* ------------------ written by attachments, read by behaviour ----------- */
    flinch: { resist: 0 },
    signature: { minimap: true, radius: SIGNATURE_RADIUS[s.classId] ?? 45, tracer: true },
    mobility: { slideFire: false },
    overheat: null,
    launcher: null,
    optic: {
      id: null,
      name: 'IRON SIGHTS',
      zoom: BASE_OPTIC_ZOOM[s.classId] ?? 1,
      baseZoom: BASE_OPTIC_ZOOM[s.classId] ?? 1,
      reticleId: 'iron',
      scopeGlint: false,
      thermalMode: false,
      targetHighlight: false,
      rangefinder: false,
      dualZoom: null,
    },
  };

  if (a.projectile ?? base.projectile) def.projectile = true;
  if (a.shellByShell ?? base.shellByShell) def.shellByShell = true;
  if (base.pumpTravel != null) def.pumpTravel = base.pumpTravel;
  if (def.fireMode === 'pump') def.pumpTime = a.pumpTime ?? 0.62;
  if (def.fireMode === 'bolt') def.boltCycle = a.boltCycle ?? 1.15;
  if (def.fireMode === 'burst') {
    def.burstCount = a.burstCount ?? 3;
    // Dead time after the last round of a burst before the next one can start.
    // Without it a burst weapon is just a full-auto that stutters, and these four
    // kill in one burst — spamming the trigger would make them the best weapons in
    // the game by a distance.
    def.burstDelay = a.burstDelay ?? 0.2;
  }
  if (a.pelletSpread != null) {
    def.pelletSpread = a.pelletSpread;
    // How far a choke may close the pattern once the stock is in the shoulder. 1 is
    // "not at all", which is the behaviour every shotgun has had until now. The aim
    // cone is still exactly zero when shouldered and stationary; this scales the
    // *shell's* pattern, which is a different quantity and always was.
    def.pelletSpreadAdsScale = 1;
  }

  const sway = a.aimSway ?? base.aimSway;
  if (sway) def.aimSway = sway;

  return def;
}

export const WEAPONS = {};
for (const id of ROSTER) WEAPONS[id] = build(id);

/**
 * The default carry set, and what slots 1..N map to before a loadout is chosen.
 *
 * All four are 初期アンロック weapons. `WeaponSystem.setLoadout` narrows this to the
 * two the progression screen picked the moment anything publishes a loadout; this
 * array is only what the player holds if nothing ever does. Handing out a level-52
 * rifle here would make the unlock ladder a lie.
 */
export const LOADOUT = ['ak27', 'ryden45k', 'm10breacher', 'vsrecon'];

/** Every weapon id, in class order. Used to build and warm all models up front. */
export const ALL_WEAPONS = ROSTER.slice();

/** Ids grouped by class, in roster order — for the loadout screen. */
export function weaponsByClass() {
  const out = {};
  for (const c of WEAPON_CLASSES) out[c.id] = [];
  for (const id of ROSTER) out[WEAPONS[id].classId].push(id);
  return out;
}

/**
 * Damage at a given range from a weapon's curve. Linear between control points and
 * clamped at both ends — a piecewise curve reads better than an exponential because
 * players can feel the "damage range" boundaries.
 */
export function damageAtRange(weapon, dist) {
  const c = weapon.damage;
  if (dist <= c[0][0]) return c[0][1];
  for (let i = 1; i < c.length; i++) {
    if (dist <= c[i][0]) {
      const [d0, v0] = c[i - 1];
      const [d1, v1] = c[i];
      const t = (dist - d0) / Math.max(1e-6, d1 - d0);
      return v0 + (v1 - v0) * t;
    }
  }
  return c[c.length - 1][1];
}

/** Seconds between shots. For a burst weapon this is the in-burst interval. */
export function shotInterval(weapon) {
  return 60 / weapon.rpm;
}

/**
 * How well a weapon's recoil pattern stands out from its own noise, at shot `n`.
 *
 * The deterministic climb after n shots is `pitch x sum(P)`; the random term is a
 * fraction `random` of each shot's magnitude, drawn uniformly, so its standard
 * deviation over n shots is `pitch x random x sqrt(sum(P^2)/3)`. The ratio is what
 * decides whether a player can learn the pattern at all — anything much over 5% and
 * the shape is buried. Exported because it is a property worth being able to assert
 * in a test rather than a claim in a comment.
 */
export function patternQuality(weapon, n = 30) {
  const p = weapon.pattern;
  const m = Math.min(n, p.length);
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < m; i++) {
    sum += p[i][0];
    sumSq += p[i][0] * p[i][0];
  }
  const signal = weapon.recoil.pitch * sum;
  const noise = weapon.recoil.pitch * weapon.recoil.random * Math.sqrt(sumSq / 3);
  return { shots: m, signal, noise, ratio: noise / signal, climbDeg: signal / DEG };
}

/**
 * Net cone growth per shot once the weapon's own decay is accounted for, at its own
 * fire rate. Must be > 0 for a blooming weapon or the cone is pinned at zero and
 * the mechanic silently does nothing.
 */
export function bloomNetPerShot(weapon) {
  const s = weapon.spread;
  return s.perShot - s.bloomDecay * (60 / weapon.rpm);
}

/**
 * Surface hardness in "penetration units per metre". A weapon's `penetration` is
 * roughly how many metres of the reference material it can defeat, so a 0.9-power
 * rifle punches through 2.5 cm of steel or 25 cm of drywall/wood but never concrete.
 */
export const PENETRATION_COST = {
  glass: 1.2,
  wood: 3.6,
  dirt: 9.0,
  sand: 11.0,
  metal: 26.0,
  concrete: 999, // effectively impenetrable
  flesh: 6.0,
  water: 4.0,
};

/** Fraction of damage that survives a full-power penetration of each material. */
export const PENETRATION_DAMAGE = {
  glass: 0.95,
  wood: 0.72,
  metal: 0.48,
  dirt: 0.55,
  sand: 0.50,
  flesh: 0.65,
  water: 0.8,
  concrete: 0.3,
};
